/**
 * WHAT COULD GO WRONG — the risks of each strategy, in plain words, from one place.
 *
 * WHY (owner, 2 Oct 2026). A card told the client what a plan does and what it produces, and said
 * nothing about what could go wrong, bar the time-horizon warning. For personal advice the risks of
 * the recommendation are part of what the client is told, so they belong on the screen where the
 * choice is made AND in the Statement of Advice — and if those two are written separately they
 * drift. This file is the single list: the cards' "What could go wrong" panel and the documents
 * both read it.
 *
 * HOW IT IS SHAPED. A strategy is made of PARTS — selling the home, buying an annuity, paying a RAD,
 * renting the home out — and each part carries the same risks whichever module put it there. So the
 * list is keyed by part (`RiskKind`), each module says which parts its strategy has, and a few
 * CLIENT-SPECIFIC points (the age-and-health warning, an assumed cost base, a stamp duty figure)
 * are added on top. Every point is one bold claim and one sentence of why: the owner's rule for this
 * screen is that a wall of text is not read.
 *
 * Nothing here is recorded about whether the client opened it (owner's decision): the Statement of
 * Advice carries the same list, and that is the record.
 */

/*
 * THE SIX-YEAR RULE FOR A HOME KEPT AND RENTED OUT, said the same way on every card, in every "What could go wrong" and
 * so in every advice document (owner, 22 Oct 2026). ITAA 1997 s 118-145: a former home can stay the main residence for
 * up to six years while it earns rent, if no other home is treated as the main residence; after that, part of the gain
 * is taxable when it is sold (B.CGT.5).
 */
export const RENTED_HOME_CGT_BODY = 'Free of it for up to six years while rented, if no other home is the main residence; after that, part of any gain is taxed on sale.';
/** The card's short form. */
export const RENTED_HOME_CGT_CARD = 'Capital gains tax: the home stays free of it for up to six years while it is rented out; after that, part of any gain can be taxed when it is sold.';

export type RiskIcon =
    | 'lock' | 'receipt' | 'home' | 'landmark' | 'trendDown' | 'clock' | 'heart' | 'percent'
    | 'users' | 'fileSignature' | 'repeat' | 'coins' | 'shield' | 'calendar' | 'wrench' | 'alert';

export interface RiskPoint {
    icon: RiskIcon;
    /** The claim, a few words, shown bold. */
    lead: string;
    /** One sentence of why it matters. */
    body: string;
}

export type RiskKind =
    | 'investmentProperty' | 'shares' | 'investmentBond'
    | 'annuityNil' | 'annuityIndexed' | 'annuityReturned' | 'annuityGuaranteed'
    | 'funeralBond' | 'gift'
    | 'superContribution' | 'accountBasedPension'
    | 'sellHome' | 'rentHome' | 'rentFormerHome' | 'sellAndRent' | 'keepHomeInCare' | 'sellInvestmentProperty' | 'transferHome'
    | 'radPaid' | 'dap' | 'reverseMortgage' | 'heas'
    | 'clearDebt' | 'fromSavings'
    | 'villageEntry' | 'grannyFlat' | 'grannyFlatBuild' | 'keepHomeEmpty' | 'moveInOnly' | 'renovate' | 'stayPut';

export interface RiskKindDetail {
    title: string;
    points: RiskPoint[];
}

export const RISK_KINDS: Record<RiskKind, RiskKindDetail> = {
    investmentProperty: {
        title: 'Buying an investment property',
        points: [
            { icon: 'lock', lead: "You can't sell part of it.", body: 'If you need money in a hurry, a property is slow and costly to turn into cash.' },
            { icon: 'receipt', lead: 'Buying costs come off the top.', body: 'Stamp duty and legal fees are paid on day one and only recovered if the property grows.' },
            { icon: 'home', lead: 'Rent can stop.', body: 'An empty property still costs rates, insurance and upkeep.' },
            { icon: 'landmark', lead: 'The rent counts.', body: "Centrelink counts the rent as income, and it's taxed." },
            { icon: 'trendDown', lead: 'Prices can fall as well as rise.', body: 'A property bought at the wrong time can be worth less than you paid for years.' },
        ],
    },
    shares: {
        title: 'Investing in shares or managed funds',
        points: [
            { icon: 'trendDown', lead: 'Values move, sometimes sharply.', body: 'A bad year can take a large amount off, and it may take years to come back.' },
            { icon: 'coins', lead: "Income isn't fixed.", body: 'Dividends and distributions can be cut without notice.' },
            { icon: 'landmark', lead: 'Centrelink deems the income.', body: 'A set rate is assumed on the balance whether or not it is actually earned.' },
            { icon: 'receipt', lead: 'Selling can mean tax.', body: 'Capital gains tax may apply when you sell.' },
        ],
    },
    investmentBond: {
        title: 'Buying an investment bond',
        points: [
            { icon: 'clock', lead: 'The tax benefit takes ten years.', body: 'Taking money out earlier can cost some of the tax advantage.' },
            { icon: 'trendDown', lead: "Returns aren't guaranteed.", body: 'The bond is invested, so its value can fall.' },
            { icon: 'percent', lead: 'Fees can be higher.', body: 'Bonds often cost more to run than a plain investment.' },
        ],
    },
    annuityNil: {
        title: 'A lifetime annuity that returns nothing on death',
        points: [
            { icon: 'lock', lead: 'The capital is gone.', body: "Once bought, the money isn't yours to take back, and nothing goes to your estate when you die." },
            { icon: 'heart', lead: 'It pays off only if you live long.', body: 'The income is guaranteed for life, so what you get back depends on how many years you receive it.' },
            { icon: 'percent', lead: 'The rate is set once.', body: "Your income is fixed when you buy and won't rise if markets or interest rates do." },
            { icon: 'fileSignature', lead: "The provider's quote is what counts.", body: 'The income shown here is an estimate; the real figure is whatever the insurer offers.' },
        ],
    },
    /*
     * INCOME ONLY, RISING WITH PRICES (guaranteed income review, 21 Oct 2026). It was tagged annuityNil, whose
     * point says "Your income is fixed" — the opposite of what this type does.
     */
    annuityIndexed: {
        title: 'A lifetime annuity that rises with prices and returns nothing on death',
        points: [
            { icon: 'lock', lead: 'The capital is gone.', body: "Once bought, the money isn't yours to take back, and nothing goes to your estate when you die." },
            { icon: 'heart', lead: 'It pays off only if you live long.', body: 'The income is guaranteed for life, so what you get back depends on how many years you receive it.' },
            { icon: 'coins', lead: 'It starts lower.', body: 'The income rises with prices each year, so the first years pay less than a level income would; it takes years to catch up.' },
            { icon: 'fileSignature', lead: "The provider's quote is what counts.", body: 'The income shown here is an estimate; the real figure is whatever the insurer offers.' },
        ],
    },
    annuityReturned: {
        title: 'A lifetime annuity that returns capital',
        points: [
            { icon: 'clock', lead: 'Less comes back over time.', body: 'What you or your estate could get back winds down on a set schedule, year by year.' },
            { icon: 'coins', lead: 'The income is lower.', body: 'Protecting the capital means a smaller payment than an annuity that returns nothing.' },
            { icon: 'percent', lead: 'The rate is set once.', body: "Your income is fixed when you buy and won't rise if markets or interest rates do." },
            { icon: 'fileSignature', lead: "The provider's quote is what counts.", body: 'The income shown here is an estimate; the real figure is whatever the insurer offers.' },
        ],
    },
    annuityGuaranteed: {
        title: 'An annuity that returns the full amount on death',
        points: [
            { icon: 'shield', lead: 'Part of your money buys insurance.', body: 'Centrelink counts that part at what you paid, so the pension lift is smaller than it looks.' },
            { icon: 'coins', lead: 'The income is the lowest of the three shapes.', body: 'Guaranteeing the full amount back costs you income every year.' },
            { icon: 'fileSignature', lead: 'The split is an estimate.', body: 'How much is annuity and how much is insurance is set by the provider, not by us.' },
            { icon: 'users', lead: "Not everyone can buy it.", body: "It's sold only to people entering or receiving government-subsidised care." },
        ],
    },
    funeralBond: {
        title: 'Putting money in a funeral bond',
        points: [
            { icon: 'lock', lead: 'The money is for a funeral only.', body: "It can't be withdrawn for anything else, however your needs change." },
            { icon: 'landmark', lead: 'Only up to the limit is exempt.', body: 'Anything above the allowable amount still counts for the Age Pension.' },
            { icon: 'trendDown', lead: 'Returns are modest.', body: 'The money grows slowly, if at all, after fees.' },
        ],
    },
    gift: {
        title: 'Giving money away',
        points: [
            { icon: 'lock', lead: 'The money is gone.', body: "Once given, you can't count on getting it back, even from family." },
            { icon: 'landmark', lead: 'Only a small amount is exempt.', body: 'Gifts above $10,000 a year, or $30,000 over five years, still count for five years.' },
            { icon: 'users', lead: 'Family circumstances change.', body: 'A divorce, a debt or a death in the family can put a gift out of reach for good.' },
        ],
    },
    superContribution: {
        title: 'Putting money into super',
        points: [
            { icon: 'landmark', lead: 'The rules can change.', body: 'Government sets how super is taxed, when you can get at it and how much you can put in.' },
            { icon: 'trendDown', lead: "Returns aren't guaranteed.", body: 'Super is invested, so the balance can fall.' },
            { icon: 'calendar', lead: 'Limits and timing apply.', body: 'A downsizer contribution goes in within 90 days of the sale, once; after-tax contributions have yearly limits and stop at 75.' },
            { icon: 'coins', lead: 'It still counts for the pension.', body: 'Once you are over pension age, Centrelink counts your super.' },
        ],
    },
    accountBasedPension: {
        title: 'Starting an account-based pension',
        points: [
            { icon: 'percent', lead: 'Minimum payments apply.', body: "You must take at least a set percentage out each year, even if you'd rather leave it." },
            { icon: 'trendDown', lead: "Returns aren't guaranteed.", body: 'The balance is invested, so it can fall in a bad year.' },
            { icon: 'landmark', lead: 'Counted by Centrelink.', body: 'The balance is counted as an asset and income is deemed on it.' },
        ],
    },
    sellHome: {
        title: 'Selling the family home',
        points: [
            { icon: 'receipt', lead: 'Selling costs real money.', body: 'Agent fees, legal costs and the move itself come out of the sale price.' },
            { icon: 'landmark', lead: 'The money is no longer exempt.', body: 'A home is exempt from the Age Pension test; money from selling it that does not go into another home is counted, so the pension can fall.' },
            { icon: 'clock', lead: "Price and timing aren't certain.", body: 'The sale may take longer, or bring less, than the figure used here.' },
            { icon: 'repeat', lead: "It's hard to undo.", body: 'Getting back into the property market later usually costs more than you got out.' },
        ],
    },
    sellAndRent: {
        title: 'Renting instead of owning',
        points: [
            { icon: 'landmark', lead: 'The whole sale is counted.', body: 'With no home, every dollar of the proceeds is in the assets test and deemed to earn income.' },
            { icon: 'trendDown', lead: 'Rent rises for life.', body: 'The rent is paid every year from the money, and it goes up with the market.' },
            { icon: 'home', lead: 'A lease can end.', body: 'A landlord can sell or raise the rent; moving again costs money and is harder with age.' },
            { icon: 'coins', lead: 'Rent Assistance is capped.', body: 'It covers part of the rent, not all of it, and only while a pension is paid.' },
            { icon: 'clock', lead: 'No home growing in the background.', body: 'A home kept its value for the estate; the money now has to earn its keep.' },
        ],
    },
    rentHome: {
        title: 'Keeping the home and renting it out',
        points: [
            { icon: 'landmark', lead: 'The rent counts.', body: "Centrelink counts the rent as income, and it's taxed." },
            { icon: 'clock', lead: 'The home may be counted later.', body: 'Once you have been out of it for a time, the home itself can be counted as an asset.' },
            { icon: 'receipt', lead: 'Capital gains tax after six years away.', body: RENTED_HOME_CGT_BODY },
            { icon: 'home', lead: 'Tenants, gaps and repairs.', body: 'Rent can stop between tenants, and repairs and rates continue regardless.' },
            { icon: 'wrench', lead: "You're a landlord now.", body: 'Managing a property, or paying someone to, takes time or money.' },
        ],
    },
    /* After a move to a village or a granny flat the old home is no longer where you live, so it counts
       from the day you move — no two-year exemption, unlike a move into care (reviews, 18 Oct 2026: the
       panel said "may be counted later" beside an explanation saying it counts straight away). */
    rentFormerHome: {
        title: 'Keeping the home and renting it out',
        points: [
            { icon: 'landmark', lead: 'The home and the rent both count.', body: "From the day you move, Centrelink counts the home as an asset and the rent as income, and the rent is taxed." },
            { icon: 'receipt', lead: 'Capital gains tax after six years away.', body: RENTED_HOME_CGT_BODY },
            { icon: 'home', lead: 'Tenants, gaps and repairs.', body: 'Rent can stop between tenants, and repairs and rates continue regardless.' },
            { icon: 'wrench', lead: "You're a landlord now.", body: 'Managing a property, or paying someone to, takes time or money.' },
        ],
    },
    keepHomeInCare: {
        title: 'Keeping the home while in care',
        points: [
            { icon: 'clock', lead: 'The exemption runs out.', body: "The home is exempt from the Age Pension test for two years after you enter care, unless a partner or protected person still lives there." },
            { icon: 'home', lead: 'An empty home still costs.', body: 'Rates, insurance, upkeep and security continue while nobody lives there.' },
            { icon: 'trendDown', lead: 'Its value can fall.', body: 'A home left empty for years can lose value and condition.' },
        ],
    },
    sellInvestmentProperty: {
        title: 'Selling an investment property',
        points: [
            { icon: 'receipt', lead: 'Capital gains tax.', body: 'Tax on the gain comes out of what you have to spend.' },
            { icon: 'coins', lead: 'The rent stops.', body: 'Income you had been living on is replaced by whatever the money earns next.' },
            { icon: 'clock', lead: 'Price and timing are not certain.', body: 'The sale may take longer, or bring less, than the figure used here.' },
        ],
    },
    transferHome: {
        title: 'Transferring the home to family',
        points: [
            { icon: 'lock', lead: 'You give up ownership.', body: 'The home is theirs; your right to live in it depends on them, and on your agreement.' },
            { icon: 'landmark', lead: 'Only what is given on top is tested.', body: "The home's value is taken as the price of the right, so the home itself is not a gift; cash or assets given as well can be, for five years." },
            { icon: 'users', lead: 'Exposed to their life.', body: 'Their divorce, debts or death can put the home out of your reach.' },
            // Owner's review, 24 Oct 2026: the transfer's costs and conditions were not said.
            { icon: 'receipt', lead: 'Stamp duty and tax.', body: 'Stamp duty may be payable, usually by the family, and is not in these figures; the home is free of capital gains tax for you.' },
            { icon: 'clock', lead: 'Leaving early can undo it.', body: 'If the arrangement ends within five years, Centrelink can look at the transfer again as a gift.' },
            { icon: 'home', lead: 'A home worth less than the homeowner line.', body: 'Then you are not homeowners for the Age Pension, and the right counts as an asset.' },
        ],
    },
    radPaid: {
        title: 'Paying the room price (RAD)',
        points: [
            { icon: 'lock', lead: 'A large sum is tied up.', body: "The money is refunded when you leave or die, but you can't use it in the meantime." },
            { icon: 'landmark', lead: 'Exempt for the pension, counted for care fees.', body: 'The RAD is not counted for the Age Pension, but it is counted in the means-tested care fee.' },
            { icon: 'clock', lead: 'The refund takes time.', body: 'Your estate receives it within 14 days of probate, under a government guarantee.' },
        ],
    },
    dap: {
        title: 'Paying for the room daily (DAP)',
        points: [
            { icon: 'percent', lead: 'It costs interest.', body: 'The daily payment is interest on the unpaid room price, at a rate set by government.' },
            { icon: 'repeat', lead: 'It never ends.', body: 'You pay it for as long as you stay, and nothing is building up to be refunded.' },
            { icon: 'coins', lead: 'It has to come from cashflow.', body: 'If income falls short, the payment is drawn from savings or added to the debt.' },
        ],
    },
    reverseMortgage: {
        title: 'A reverse mortgage',
        points: [
            { icon: 'trendDown', lead: 'The debt grows.', body: 'Interest is added to the loan, so what you owe rises every year even with no repayments.' },
            { icon: 'home', lead: 'Less left for the family.', body: 'The loan and all its interest are repaid from the home when it is sold.' },
            { icon: 'percent', lead: 'Rates can rise.', body: 'A higher rate means the debt grows faster.' },
            { icon: 'fileSignature', lead: 'Conditions apply.', body: 'The lender can require upkeep and insurance, and moving out can trigger repayment.' },
        ],
    },
    heas: {
        title: 'The Home Equity Access Scheme',
        points: [
            { icon: 'trendDown', lead: 'The debt grows.', body: 'Interest compounds at the government rate, so what you owe rises every year.' },
            { icon: 'home', lead: 'Repaid from the home.', body: 'The loan and its interest come out of the home when it is sold, or from your estate.' },
            { icon: 'coins', lead: 'Payments are capped.', body: 'You can draw up to 150% of the full pension, counting any pension you already receive.' },
        ],
    },
    clearDebt: {
        title: 'Paying off debt',
        points: [
            { icon: 'coins', lead: 'Less in reserve.', body: "Money used to clear a debt isn't there for an emergency." },
            { icon: 'repeat', lead: 'You may not get it back.', body: 'Once repaid, a loan may not be available to draw on again.' },
        ],
    },
    fromSavings: {
        title: 'Paying from savings',
        points: [
            { icon: 'coins', lead: 'Less in reserve.', body: 'A smaller buffer means less room for a surprise cost or a bad year.' },
            { icon: 'receipt', lead: 'Selling investments can mean tax.', body: 'Shares sold to raise the money may trigger capital gains tax.' },
        ],
    },
    villageEntry: {
        title: 'Moving into a retirement village',
        points: [
            { icon: 'fileSignature', lead: "You're buying a contract, not a home.", body: 'Leaving costs real money, through the departure fee.' },
            { icon: 'clock', lead: 'Your exit money waits for a re-sale.', body: 'You or your estate may wait months, sometimes years, for the refund.' },
            { icon: 'coins', lead: 'Fees continue.', body: 'Ongoing fees are payable, and in some states for a time after you leave.' },
            { icon: 'repeat', lead: 'A later move into care means moving twice.', body: 'Each move has its own costs and its own wait for money to come back.' },
        ],
    },
    grannyFlat: {
        title: 'A granny flat arrangement',
        points: [
            { icon: 'users', lead: "You're relying on family.", body: 'Your right to live there depends on the relationship holding.' },
            { icon: 'fileSignature', lead: 'Get it in writing.', body: 'Without a written agreement you have little protection if things change.' },
            { icon: 'landmark', lead: 'Paying too much counts as a gift.', body: 'Centrelink can treat anything above a reasonable value as a gift for five years.' },
            { icon: 'home', lead: 'The money becomes theirs.', body: 'What you pay joins their assets, exposed to their divorce, debts or death.' },
        ],
    },
    // A build paid for at the family member's home (owner's review, 24 Oct 2026: a build got the cash warning
    // "paying too much counts as a gift", which a build on its own can never do).
    grannyFlatBuild: {
        title: 'Paying for a build at their home',
        points: [
            { icon: 'users', lead: "You're relying on family.", body: 'Your right to live there depends on the relationship holding.' },
            { icon: 'fileSignature', lead: 'Get it in writing.', body: 'Without a written agreement you have little protection if things change.' },
            { icon: 'receipt', lead: 'Builds run over.', body: 'Approvals, delays and over-runs are common; keep the contract and invoices, as anything paid beyond the build’s cost is tested.' },
            { icon: 'home', lead: 'The building belongs to the family member.', body: 'It becomes part of the host’s property, exposed to their divorce, debts or death; what is bought is the right to live there.' },
        ],
    },
    keepHomeEmpty: {
        title: 'Keeping the home, empty',
        points: [
            { icon: 'landmark', lead: 'It is counted from the day you move.', body: 'With no one living in it, the home is counted in full for the Age Pension, and it earns nothing.' },
            { icon: 'receipt', lead: 'It still costs money.', body: 'Rates, insurance and upkeep go on, and an empty home can need a vacancy policy; these costs are not in the figures unless entered.' },
        ],
    },
    // Moving in with family and paying nothing (gf-move-in-only): no granny flat right, but real risks all the
    // same (live review, 22 Oct 2026: the plan showed "These are the 0 that matter").
    moveInOnly: {
        title: 'Moving in without paying for a right',
        points: [
            { icon: 'fileSignature', lead: 'No right to stay.', body: 'Nothing is paid and nothing is signed, so you have no legal right to live there if things change.' },
            { icon: 'users', lead: "You're relying on family.", body: 'Where you live depends on the relationship, and on their own plans for their home.' },
            { icon: 'home', lead: 'The empty home still costs money.', body: 'Rates, insurance and upkeep go on, and an empty home can need a vacancy policy; it is counted for the Age Pension from the day you move.' },
        ],
    },
    renovate: {
        title: 'Spending on the home',
        points: [
            { icon: 'receipt', lead: 'Costs can run over.', body: 'Building work rarely comes in at the first quote.' },
            { icon: 'lock', lead: 'The money is tied up in the home.', body: "It isn't there to spend, and you only get it back by selling." },
            { icon: 'trendDown', lead: 'You may not get it all back.', body: 'Not every dollar spent on a home adds a dollar to its value.' },
        ],
    },
    stayPut: {
        title: 'Changing nothing',
        points: [
            { icon: 'trendDown', lead: 'Costs keep rising.', body: 'Fees, rates and living costs go up each year whether or not your income does.' },
            { icon: 'clock', lead: 'A move may be forced later.', body: 'If care is needed, the decision may have to be made quickly and at a worse time.' },
            { icon: 'alert', lead: 'Nothing improves.', body: 'The pension, cashflow and estate stay where they are today.' },
        ],
    },
};

/** Client-specific points, worked out by the module from the strategy and the file. */
export interface StrategyRiskSpecifics {
    /** The age-and-health time-horizon warning, as the engine words it. */
    horizonWarning?: string | null;
    /** The stamp duty a property purchase pays, in dollars. */
    stampDuty?: number | null;
    /** True when a cost base was assumed because the client did not enter one. */
    cgtAssumed?: boolean;
    /** The year savings run out under this plan, if they do. */
    savingsRunOutYear?: number | null;
    /**
     * Estate equalisation (owner, 2 Oct 2026): the plan hands wealth to ONE family member and the
     * client has more than one child. What goes now comes out of what the others inherit later, so
     * it is raised wherever the choice is made — the panel and the Statement of Advice.
     */
    familyToKeepWhole?: { childrenCount: number; amount: number; what: 'home' | 'payment' } | null;
}

export interface StrategyRisks {
    /** Points that are about this client, shown first. */
    forYou: RiskPoint[];
    /** One section per part of the strategy, in the order given. */
    sections: { kind: RiskKind; title: string; points: RiskPoint[] }[];
}

const money = (n: number): string => `$${Math.round(n).toLocaleString('en-AU')}`;

/** The list for a strategy: its parts' risks, with the client-specific points on top. */
export function buildStrategyRisks(kinds: readonly RiskKind[], specifics: StrategyRiskSpecifics = {}): StrategyRisks {
    const forYou: RiskPoint[] = [];
    const fam = specifics.familyToKeepWhole;
    if (fam && fam.childrenCount > 1 && fam.amount > 0) {
        forYou.push({
            icon: 'users',
            lead: `One of your ${fam.childrenCount} children receives ${fam.what === 'home' ? 'the home' : money(fam.amount)}; ${fam.childrenCount === 2 ? 'the other does not' : 'the others do not'}.`,
            body: `${fam.what === 'home' ? `The ${money(fam.amount)} home` : 'What you pay'} goes to one family now and comes out of what the rest inherit later. Decide how to keep it fair — the Will, an advance on their inheritance, or insurance — and write it down; the family discussion guide covers it.`,
        });
    }
    if (specifics.horizonWarning) {
        forYou.push({ icon: 'heart', lead: 'Time may be against this plan.', body: specifics.horizonWarning });
    }
    if (specifics.savingsRunOutYear != null) {
        forYou.push({ icon: 'coins', lead: `Savings run out in year ${specifics.savingsRunOutYear}.`, body: 'After that, the shortfall each year has to come from somewhere else.' });
    }
    if (specifics.stampDuty != null && specifics.stampDuty > 0) {
        forYou.push({ icon: 'receipt', lead: `${money(specifics.stampDuty)} of stamp duty is paid on day one.`, body: 'It comes out of the sale proceeds and is only recovered if the property grows by at least that much.' });
    }
    if (specifics.cgtAssumed) {
        forYou.push({ icon: 'receipt', lead: 'The tax figure is an assumption.', body: "You didn't enter what an asset cost you, so we assumed 25% of its value, which usually overstates the tax." });
    }
    const seen = new Set<RiskKind>();
    const sections = kinds
        .filter((k) => (seen.has(k) ? false : (seen.add(k), true)))
        .map((kind) => ({ kind, title: RISK_KINDS[kind].title, points: RISK_KINDS[kind].points }));
    return { forYou, sections };
}

/** Plain-text lines for a document: "Title — Lead Body". */
export function strategyRiskLines(risks: StrategyRisks): string[] {
    return [
        ...risks.forYou.map((p) => `${p.lead} ${p.body}`),
        ...risks.sections.flatMap((s) => s.points.map((p) => `${s.title} — ${p.lead} ${p.body}`)),
    ];
}

/* ─── Which parts a strategy has, module by module ────────────────────────────────────────────── */

/** The candidate ids the village, downsizer, granny flat and cashflow engines use, read by suffix. */
export function riskKindsFromCandidateId(id: string): RiskKind[] {
    const kinds: RiskKind[] = [];
    const has = (re: RegExp) => re.test(id);
    const bare = id.replace(/-clear-debt$/, '');
    if (/^rv-/.test(bare) && bare !== 'rv-stay-home') kinds.push('villageEntry');
    // A hand-built sale is 'gf-sell' with no suffix (24 Oct 2026): it gets the selling risks too.
    if (/^gf-/.test(bare) && !/^gf-(stay-put|sell(-|$)|move-in-only)/.test(bare)) kinds.push('grannyFlat');
    if (/^gf-sell(-|$)/.test(bare)) kinds.push('grannyFlat');
    if (/^gf-move-in-only/.test(bare)) kinds.push('moveInOnly');
    if (/^(rv-stay-home|dz-stay-put|gf-stay-put|cp-nothing)$/.test(bare)) return ['stayPut'];
    if (has(/sell-home|^dz-(rent-)?(price|simple|super|invest|annuity|funeral|gift|your-plan)|^gf-sell(-|$)/)) kinds.push('sellHome');
    // Downsizer candidates that sell and RENT (2 Oct 2026): a renting household, said as its own part.
    if (has(/^dz-rent-/)) kinds.push('sellAndRent');
    if (has(/^rv-sell-ip|sell-ip/)) kinds.push('sellInvestmentProperty');
    if (has(/^gf-transfer-home/)) kinds.push('transferHome');
    if (has(/liquid-rent|keep-rent/)) kinds.push('rentFormerHome');
    if (has(/^rv-liquid|^gf-(fair-cash|known)/)) kinds.push('fromSavings');
    if (has(/-abp-ip$|-property$|invest-property/)) kinds.push('investmentProperty');
    if (has(/-abp-invest$|-invest$|^dz-invest$|liquid-shares|-shares$/)) kinds.push('shares');
    if (has(/invest-bond|super-and-bond|-bond$/) && !has(/funeral/)) kinds.push('investmentBond');
    if (has(/downsizer|super-after-tax|super-max|^dz-super|sell-super|-super$|-super-and|-super-(abp|invest|annuity)/)) kinds.push('superContribution');
    if (has(/-abp/)) kinds.push('accountBasedPension');
    if (has(/annuity-guaranteed/)) kinds.push('annuityGuaranteed');
    else if (has(/annuity-rcv/)) kinds.push('annuityReturned');
    else if (has(/annuity/)) kinds.push('annuityNil');
    if (has(/funeral-bond|bond-and-gift/)) kinds.push('funeralBond');
    if (has(/-gift$|bond-and-gift/)) kinds.push('gift');
    if (/-clear-debt$/.test(id)) kinds.push('clearDebt');
    return kinds;
}

/**
 * A granny flat plan's risks from what it actually does, not only its id (owner's review, 24 Oct 2026): a build
 * and a cash payment share the id 'gf-known', and a home kept empty had no entry. The build replaces the cash
 * warning (a build on its own is never a gift); cash on top of it keeps it.
 */
export function gfRiskKinds(id: string, m: { construction?: number; amountPaid?: number; home?: string | null }): RiskKind[] {
    let kinds = riskKindsFromCandidateId(id);
    if ((m.construction || 0) > 0) {
        const cashOnTop = (m.amountPaid || 0) > 0;
        kinds = kinds.flatMap((k) => (k === 'grannyFlat' ? (cashOnTop ? ['grannyFlatBuild', 'grannyFlat'] as RiskKind[] : ['grannyFlatBuild'] as RiskKind[]) : [k]));
        if (!kinds.includes('grannyFlatBuild')) kinds.unshift('grannyFlatBuild');
    }
    if (m.home === 'kept' && !kinds.includes('keepHomeEmpty') && !kinds.includes('moveInOnly')) kinds.push('keepHomeEmpty');
    return kinds;
}

/** Support at Home candidates carry a config type. */
export function riskKindsFromSupportAtHomeType(type: string | undefined, name: string = ''): RiskKind[] {
    const n = name.toLowerCase();
    switch (type) {
        case 'doNothing': return ['stayPut'];
        case 'gifting': return ['gift'];
        case 'funeralBonds': return ['funeralBond'];
        case 'annuity':
            return [n.includes('guaranteed') ? 'annuityGuaranteed' : n.includes('capital returned') ? 'annuityReturned' : 'annuityNil'];
        case 'increaseHomeEquity':
        case 'renovate':
        case 'upgrade': return ['renovate'];
        case 'downsize': {
            const kinds: RiskKind[] = ['sellHome'];
            if (n.includes('downsizer')) kinds.push('superContribution');
            if (n.includes('abp')) kinds.push('accountBasedPension');
            if (n.includes('repay')) kinds.push('clearDebt');
            return kinds;
        }
        case 'reverseMortgage': return ['reverseMortgage'];
        case 'heas': return ['heas'];
        case 'debtRepayment': return ['clearDebt'];
        case 'combination': {
            const kinds: RiskKind[] = [];
            if (n.includes('gift')) kinds.push('gift');
            if (n.includes('funeral')) kinds.push('funeralBond');
            if (n.includes('annuity')) kinds.push(n.includes('guaranteed') ? 'annuityGuaranteed' : n.includes('capital returned') ? 'annuityReturned' : 'annuityNil');
            if (n.includes('downsize')) kinds.push('sellHome');
            if (n.includes('reverse mortgage')) kinds.push('reverseMortgage');
            if (n.includes('heas')) kinds.push('heas');
            return kinds;
        }
        default: return [];
    }
}

/** Aged care strategies are named, not id'd; the name and a few flags say what the plan is made of. */
export function riskKindsFromAgedCareStrategy(strategy: {
    name?: string;
    meansTestStrategy?: { type?: string } | null;
    equityRelease?: { kind?: string } | null;
    strategyOptions?: { downsizeHome?: boolean; rentHome?: boolean; sellInvestmentProperty?: boolean; debtPayoff?: unknown } | null;
}): RiskKind[] {
    const name = String(strategy.name || '');
    const kinds: RiskKind[] = [];
    const pct = /(\d+)% RAD/.exec(name);
    const radPct = pct ? Number(pct[1]) : 0;
    if (radPct > 0) kinds.push('radPaid');
    if (radPct < 100) kinds.push('dap');
    if (strategy.strategyOptions?.downsizeHome || /Downsize/i.test(name)) kinds.push('sellHome');
    else if (strategy.strategyOptions?.rentHome || /Rent Home/i.test(name)) kinds.push('rentHome');
    else if (/Keep Home/i.test(name) || name === 'Do Nothing') kinds.push('keepHomeInCare');
    if (strategy.strategyOptions?.sellInvestmentProperty || /Sell .*(Keep|Rent) Home/.test(name)) kinds.push('sellInvestmentProperty');
    if (/max downsizer|downsizer ABP/i.test(name)) kinds.push('superContribution');
    if (/downsizer ABP/i.test(name)) kinds.push('accountBasedPension');
    const mt = strategy.meansTestStrategy?.type;
    if (mt === 'annuity' || /annuity/i.test(name)) {
        const indexed = /rising with inflation/i.test(name) || (strategy.meansTestStrategy as { indexation?: boolean } | null | undefined)?.indexation === true;
        kinds.push(/full amount guaranteed/i.test(name) ? 'annuityGuaranteed' : /capital returned/i.test(name) ? 'annuityReturned' : indexed ? 'annuityIndexed' : 'annuityNil');
    }
    if (mt === 'funeralBonds' || /funeral bond/i.test(name)) kinds.push('funeralBond');
    if (mt === 'gifting' || /gift/i.test(name)) kinds.push('gift');
    const er = strategy.equityRelease?.kind;
    if (er === 'heas' || /Home Equity Access Scheme/i.test(name)) kinds.push('heas');
    else if (er || /Reverse mortgage/i.test(name)) kinds.push('reverseMortgage');
    if (strategy.strategyOptions?.debtPayoff || /Clear Debt|Pay Down Debt/i.test(name)) kinds.push('clearDebt');
    if (radPct > 0 && /\(.*->.*\)/.test(name)) kinds.push('fromSavings');
    return kinds;
}
